/**
 * AGENT SHARE LINKS (2026-11-01).
 *
 * The link is the product an agent sells with, so it gets a page of its own with ready-to-send copy rather
 * than one input buried on the overview.
 */
import React, { useState } from "react";
import {
  AgentPageState,
  styles,
  useAgentResource,
} from "./agentShared";

const buildMessages = (link, code) => [
  {
    key: "sms",
    label: "Text message",
    body: `I use OGESTYLE for my beauty business — bookings, payments and client records in one place. Here's my link if you want to try it: ${link}`,
  },
  {
    key: "email",
    label: "Email",
    body: `Hi,\n\nI wanted to share the platform my business runs on. OGESTYLE handles bookings, payments, staff and client history in one place, and they charge per professional rather than per shop.\n\nYou can sign up here: ${link}\n\nUse my code ${code} so they know I sent you.\n\nBest,`,
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    body: `Try OGESTYLE for your shop — bookings, payments and client records in one place. ${link} (code ${code})`,
  },
];

export default function AgentLinks() {
  const { loading, error, unavailable, data, reload } = useAgentResource(
    "/api/platform-plans/referral/summary",
  );
  const [copied, setCopied] = useState("");

  const link = data?.agent?.link || "";
  const code = data?.agent?.code || "";

  const copy = async (value, key) => {
    if (!value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const el = document.createElement("textarea");
        el.value = value;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopied(key);
      window.setTimeout(() => setCopied(""), 2000);
    } catch (_) {
      setCopied("");
    }
  };

  return (
    <AgentPageState
      loading={loading}
      error={error}
      unavailable={unavailable}
      reload={reload}
    >
      <div style={styles.page}>
        <h1 style={styles.h1}>Share links</h1>
        <p style={{ ...styles.muted, marginTop: 0 }}>
          One link, one code. Whatever a business signs up with, it is attributed to
          you for good — a later code cannot take it away.
        </p>

        <div style={styles.card}>
          <h2 style={styles.h2}>Your link</h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            <input
              readOnly
              value={link}
              style={{ ...styles.input, flex: "1 1 320px" }}
              aria-label="Your referral link"
            />
            <button
              type="button"
              style={styles.btn}
              onClick={() => copy(link, "link")}
            >
              {copied === "link" ? "Copied" : "Copy"}
            </button>
          </div>
          <h2 style={{ ...styles.h2, marginTop: "20px" }}>Your code</h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            <input
              readOnly
              value={code}
              style={{ ...styles.input, fontFamily: "monospace", letterSpacing: "0.08em" }}
              aria-label="Your referral code"
            />
            <button
              type="button"
              style={styles.btnGhost}
              onClick={() => copy(code, "code")}
            >
              {copied === "code" ? "Copied" : "Copy code"}
            </button>
          </div>
        </div>

        <div style={styles.card}>
          <h2 style={styles.h2}>Ready to send</h2>
          {buildMessages(link, code).map((message) => (
            <div
              key={message.key}
              style={{
                borderTop: "1px solid #f1f5f9",
                paddingTop: "14px",
                marginTop: "14px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "8px",
                }}
              >
                <strong style={{ fontSize: "14px" }}>{message.label}</strong>
                <button
                  type="button"
                  style={styles.btnGhost}
                  onClick={() => copy(message.body, message.key)}
                >
                  {copied === message.key ? "Copied" : "Copy"}
                </button>
              </div>
              <p
                style={{
                  ...styles.muted,
                  whiteSpace: "pre-wrap",
                  margin: 0,
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  padding: "10px 12px",
                }}
              >
                {message.body}
              </p>
            </div>
          ))}
        </div>

        <div style={styles.card}>
          <h2 style={styles.h2}>How commission works</h2>
          <ul style={{ ...styles.muted, margin: 0, paddingLeft: "18px" }}>
            <li>
              You earn {data?.agent?.commissionPercent ?? "—"}% of what OGESTYLE
              collects, after card fees — not of list price.
            </li>
            <li>Nothing is earned during a business’s free trial.</li>
            <li>
              {data?.agent?.commissionCapMonths
                ? `Commission runs for ${data.agent.commissionCapMonths} months per referred location.`
                : "Commission is open-ended for every location you refer."}
            </li>
            <li>A refund or chargeback reverses the commission it paid.</li>
          </ul>
        </div>
      </div>
    </AgentPageState>
  );
}
